import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import Badge from '@/app/common/components/Badge'
import { Button } from '@/app/common/components/Button'
import Image from '@/app/common/components/Image/Image'
import { formatPhoneNumber as rawFormatPhoneNumber } from '@/app/common/utilities'
import ContactEmail from '@/app/pages/marketplace/components/ContactEmail'
import ContactPhone from '@/app/pages/marketplace/components/ContactPhone'
import ContactWebsite from '@/app/pages/marketplace/components/ContactWebsite'
import CardItem from '@/app/pages/marketplace/components/CardItem'
import { TABLE_FILTERS } from '@/app/pages/marketplace/constants'
import { getAcceptingNewClientsLabelKey } from '@/app/pages/marketplace/utils'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import { Input } from '@/common/components/Input.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SimpleSelector, {
  ListItem,
} from '@/common/components/SimpleSelector.tsx'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import {
  BackIcon,
  FilterIcon,
  LocationIcon,
  MagnifierIcon,
} from '@/common/icons/general.tsx'
import { MarketplaceStore } from '@/queries/marketplace/schema'
import { MarketplaceProvider } from '@/queries/providers/schema'
import { Popover, PopoverContent, PopoverTrigger } from '@/shad/ui/popover'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

// Utility function to determine best contrast text color (unchanged)
function getContrastTextColor(bgColor?: string | null): string {
  // Default to black text if no background color is provided
  if (!bgColor) return '#000000'

  // Handle different color formats
  let r = 0,
    g = 0,
    b = 0

  // Handle hex colors
  if (bgColor.startsWith('#')) {
    const hex = bgColor.replace('#', '')

    // Handle shorthand hex format (#RGB)
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16)
      g = parseInt(hex[1] + hex[1], 16)
      b = parseInt(hex[2] + hex[2], 16)
    }
    // Handle standard hex format (#RRGGBB)
    else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16)
      g = parseInt(hex.substring(2, 4), 16)
      b = parseInt(hex.substring(4, 6), 16)
    } else {
      return '#000000' // Invalid format, default to black
    }
  }
  // Handle rgb/rgba format
  else if (bgColor.startsWith('rgb')) {
    const rgbValues = bgColor.match(/\d+/g)
    if (rgbValues && rgbValues.length >= 3) {
      r = parseInt(rgbValues[0])
      g = parseInt(rgbValues[1])
      b = parseInt(rgbValues[2])
    } else {
      return '#000000' // Invalid format, default to black
    }
  } else {
    return '#000000' // Unsupported format, default to black
  }

  // Calculate relative luminance according to WCAG 2.0
  // First, normalize RGB values
  const sR = r / 255
  const sG = g / 255
  const sB = b / 255

  // Apply gamma correction
  const R = sR <= 0.03928 ? sR / 12.92 : Math.pow((sR + 0.055) / 1.055, 2.4)
  const G = sG <= 0.03928 ? sG / 12.92 : Math.pow((sG + 0.055) / 1.055, 2.4)
  const B = sB <= 0.03928 ? sB / 12.92 : Math.pow((sB + 0.055) / 1.055, 2.4)

  // Calculate relative luminance using WCAG formula
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B

  // Use WCAG recommended contrast threshold (higher than 0.5)
  // This gives better accessibility
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

// Adapter to allow formatPhoneNumber to accept string | null | undefined
const formatPhoneNumber = (phone: string | null | undefined) => {
  if (!phone) return ''
  return rawFormatPhoneNumber(phone)
}

export interface ProviderItemsProps {
  items: MarketplaceItem[]
  search: string
  setSearch: (val: string) => void
  setSelectedFilter: (item: string) => void
  onItemClick: (item: MarketplaceItem) => void
  provider: MarketplaceProvider | MarketplaceStore
  currentPage: number
  perPage: number
  totalRecords: number
  setCurrentPage: (page: number) => void
  setPerPage: (value: number) => void
}

const ProviderItems = ({
  items,
  provider,
  search,
  setSearch,
  setSelectedFilter,
  currentPage,
  perPage,
  totalRecords,
  setCurrentPage,
  setPerPage,
  onItemClick,
}: ProviderItemsProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const bgColor = provider?.header_background_color ?? undefined
  const textColor = getContrastTextColor(bgColor)

  const handleFilterOnChange = (item: ListItem) => {
    const filterValue =
      item.id === 0 ? '' : item.id === 1 ? '1,2' : item.id.toString()
    setSelectedFilter(filterValue)
  }

  const goBack = () => {
    navigate(-1)
  }

  // Contact information availability check
  const hasContactInfo =
    provider?.customer_service_phone ||
    provider?.website ||
    provider?.customer_service_email

  const streetAddress = provider?.address?.trim()
  const cityState = [provider?.city, provider?.global_state_name]
    .filter(Boolean)
    .join(', ')
  const localityLine = [cityState, provider?.zipcode].filter(Boolean).join(' ')
  const hasAddress = Boolean(streetAddress || localityLine)

  const affiliations = 'affiliations' in provider ? provider.affiliations : []
  const businessCategoryName =
    'global_business_category_name' in provider
      ? provider.global_business_category_name
      : (provider.provider_application?.business_category_name ?? '')
  const notAcceptingLabelKey = getAcceptingNewClientsLabelKey({
    isAcceptingNewClients: false,
    businessCategoryName,
  })

  const getAffiliationInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean)

    if (parts.length === 0) return ''
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: bgColor,
          color: textColor,
        }}
        className="ml-2 text-left p-4 rounded-md text-md font-semibold mb-8"
      >
        <div className="flex gap-x-4">
          <div>
            {provider.logo ? (
              <Image className="h-[6rem] rounded-md" path={provider?.logo} />
            ) : (
              <div className="h-[6rem] w-[6rem] bg-gray-200 rounded-md flex items-center justify-center">
                No Image
              </div>
            )}
          </div>
          <div className="flex items-center justify-beteween w-full">
            <div className="flex items-center justify-between w-full">
              <div>
                <div className="">{provider?.name}</div>
                <div className="text-sm opacity-90 font-normal">
                  {provider?.organization_description}
                </div>
                <div>
                  <div className="font-normal opacity-90 text-sm mt-4">
                    <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-3">
                      {hasAddress && (
                        <>
                          <span aria-hidden="true" className="pt-0.5">
                            <LocationIcon />
                          </span>

                          <address className="not-italic">
                            {streetAddress && (
                              <div className="leading-[100%]">
                                {streetAddress}
                              </div>
                            )}
                            {localityLine && <div>{localityLine}</div>}
                          </address>
                        </>
                      )}

                      {affiliations.length > 0 && (
                        <>
                          <div aria-hidden="true" />
                          <div className="space-y-2">
                            <div className="text-xs font-medium uppercase tracking-wide opacity-80">
                              Affiliations
                            </div>

                            <ul
                              className="flex flex-wrap items-center gap-1.5"
                              role="list"
                            >
                              {affiliations.map((affiliation) => (
                                <li
                                  key={affiliation.id}
                                  title={affiliation.name}
                                  className="flex size-10 items-center justify-center rounded-full border border-white/60 bg-white shadow-sm"
                                >
                                  <span className="sr-only">
                                    {affiliation.name}
                                  </span>

                                  {affiliation.logo ? (
                                    <Image
                                      className="max-h-6 max-w-6 object-contain"
                                      path={affiliation.logo}
                                      alt=""
                                    />
                                  ) : (
                                    <span
                                      aria-hidden="true"
                                      className="text-xs font-semibold text-gray-600"
                                    >
                                      {getAffiliationInitials(affiliation.name)}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Contact Information Popover */}
              {hasContactInfo && (
                <div className="ml-6 mt-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant={'outline'}>About us</Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-72 p-4 ">
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <h4 className="font-medium mb-2">
                            Store Information
                          </h4>
                          <div className="ml-2 space-y-1">
                            {provider?.website && (
                              <ContactWebsite website={provider.website} />
                            )}

                            <ContactPhone
                              phone={provider.phone_store ?? 'Not Available'}
                              formatPhoneNumber={formatPhoneNumber}
                            />
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Customer Service</h4>
                          <div className="ml-2 space-y-1">
                            <ContactEmail
                              email={
                                provider.customer_service_email ??
                                'Not Available'
                              }
                            />
                            <ContactPhone
                              phone={
                                provider.customer_service_phone ??
                                'Not Available'
                              }
                              formatPhoneNumber={formatPhoneNumber}
                              label="Phone"
                            />
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Payments Support</h4>
                          <div className="ml-2 space-y-1">
                            <ContactEmail
                              email={
                                provider.payments_support_email ??
                                'Not available'
                              }
                            />

                            <ContactPhone
                              phone={
                                provider.payments_support_phone ??
                                'Not available'
                              }
                              formatPhoneNumber={formatPhoneNumber}
                            />
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Card className="overflow-visible">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="flex items-center justify-center">
            <button
              onClick={goBack}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <BackIcon />
            </button>
            <div className="flex gap-2">
              <div>
                {totalRecords} {totalRecords === 1 ? 'Item' : 'Items'}
              </div>
              <div className="hidden md:block">
                {provider.accepting_new_clients === 0 && (
                  <Badge semanticColor="Negative">
                    {t(notAcceptingLabelKey)}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <TableToolBar>
            <div className="relative rounded-md shadow-sm w-full">
              <Input
                leftIcon={<MagnifierIcon />}
                className="shadow-none"
                placeholder="Search by Name or Type"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <SimpleSelector
              list={TABLE_FILTERS}
              onChange={handleFilterOnChange}
              className="pt-4 lg-sm:ps-4 lg-sm:pt-0"
              icon={<FilterIcon className="text-primary" />}
            />
          </TableToolBar>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col">
            {items && items.length > 0 ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                {items.map((item) => (
                  <CardItem
                    key={item.id}
                    provider={provider}
                    onItemClick={onItemClick}
                    item={item}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-10">
                No store items were found.
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="p-0 border-t border-transparent mt-5">
          <Pagination
            itemsPerPage={perPage}
            currentPage={currentPage}
            totalRows={totalRecords}
            onPageChange={setCurrentPage}
            onPerPageChange={setPerPage}
          />
        </CardFooter>
      </Card>
    </div>
  )
}
export default ProviderItems
